import axios from 'axios';
import authService from './authService';
import { getApiBaseUrl } from './apiConfig';

const API_BASE = `${getApiBaseUrl()}/api/claims`;
const withAuth = () => ({ headers: authService.getAuthHeader() });

export type ClaimStatus = 'pending' | 'accepted' | 'declined' | 'expired';

export interface ClaimRecord {
    id: string;
    entityType: string;
    entityId: string;
    invitedBy: string;
    invitedByEntityId: string;
    email: string;
    status: ClaimStatus;
    expiresAt: string;
    acceptedBy: string | null;
    acceptedAt: string | null;
    createdAt: string;
    updatedAt: string;
}

/** Public claim record returned by GET /:token — safe for unauthenticated display. */
export interface PublicClaimRecord {
    id: string;
    entityType: string;
    entityId: string;
    /** The stub's name; null when the row is gone. */
    entityName?: string | null;
    /** The workspace that sent the claim; null when the row is gone. */
    invitedByName?: string | null;
    email: string;
    status: ClaimStatus;
    expiresAt: string;
}

/**
 * Ownership handoff for stub entities someone created on another's behalf.
 * Claim tokens are delivered via email and consumed from the public
 * `/claim/:token` page.
 */
export const claimApi = {
    /**
     * Send a claim invitation. `invitedByEntityId` must be the workspace that
     * keeps the stub (its maintainer); anything else is a 403. The claim
     * token goes to the addressee only and is never returned here.
     */
    async send(args: {
        entityType: string;
        entityId: string;
        email: string;
        /** Optional: texts the same link. Email stays required. */
        phone?: string;
        invitedByEntityId: string;
        expiresInDays?: number;
    }): Promise<{ claim: ClaimRecord }> {
        const res = await axios.post(API_BASE, args, withAuth());
        return res.data;
    },

    /** List outgoing claims from a given entity (owner-only). */
    async list(entityId: string, status?: ClaimStatus): Promise<ClaimRecord[]> {
        const res = await axios.get(API_BASE, {
            params: { entityId, status },
            ...withAuth(),
        });
        return res.data;
    },

    /** Public token lookup — no auth required. */
    async getByToken(token: string): Promise<PublicClaimRecord> {
        const res = await axios.get(`${API_BASE}/${token}`);
        return res.data;
    },

    /** Accept a claim — promotes the stub into a new ManagedEntity owned by the caller. */
    async accept(token: string): Promise<{
        managedEntity: any;
        entity: any;
        claim: ClaimRecord;
    }> {
        const res = await axios.post(`${API_BASE}/${token}/accept`, {}, withAuth());
        return res.data;
    },

    /** Decline a claim. Auth optional — the token is sufficient. */
    async decline(token: string): Promise<ClaimRecord> {
        const res = await axios.post(`${API_BASE}/${token}/decline`, {}, withAuth());
        return res.data;
    },

    /** Cancel a pending claim as the original inviter. */
    async cancel(claimId: string): Promise<void> {
        await axios.delete(`${API_BASE}/${claimId}`, withAuth());
    },
};

export default claimApi;
